import React, { Fragment, useCallback } from 'react';
import uniqueId from 'lodash.uniqueid';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import { default as DefaultTable } from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';

import TableSkeleton from '../SkeletonUI/SkeletonComponents/TableSkeleton';

import Typography from '../Typography';

import { TableProps, TableConfigProps, TableCellType } from './Table.types';

const useStyle = makeStyles({
  root: {
    borderBottom: 'none',
  }
});

const Table: React.FC<TableProps> = ({
  className,
  headerClassName,
  config,
  padding,
  size,
  data,
  stickyHeader,
  showHeader,
  children,
  innerTableRender,
  innerTableVisibleMap,
  keyName,
  loading
}) => {
  const classes = useStyle();

  const getTableHeader = useCallback(() => {
    if (showHeader) {
      return (
        <TableHead className={clsx('table-header', headerClassName)}>
          <TableRow>
            {config?.map((column: TableConfigProps) => {
              const { label, right, columnVariant = 'SemiBold200', cellClassName} = column;

              let labelValue = null;
              if (typeof label === 'function') {
                labelValue = label();
              } else {
                labelValue = (
                  <Typography variant={columnVariant}>{label}</Typography>
                );
              }

              return (
                <TableCell key={uniqueId()} align={right ? 'right' : 'left'} className={clsx(classes.root, cellClassName)}>
                  {labelValue}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
      );
    }

    return null;
  }, [showHeader, config]);

  const getTableRow = useCallback(
    (row, index) => {
      return (
        <>
          <TableRow key={row?.hasOwnProperty(keyName) ? row?.[keyName] : uniqueId()}>
            {config?.map((column: TableConfigProps,iIndex: number) => {
              const {
                right,
                selector,
                cell,
                valueVariant = 'Regular200',
                cellClassName
              } = column;

              let cellValue: TableCellType = (
                <Typography variant={valueVariant}>{row?.[selector]}</Typography>
              );
              if (typeof cell === 'function') {
                cellValue = cell(row, index);
              } else if (cell) {
                cellValue = cell;
              }

              return (
                <TableCell
                  key={row?.hasOwnProperty(keyName) ? `${row?.[keyName]}-${iIndex}` : uniqueId()}
                  align={right ? 'right' : 'left'}
                  className={clsx(classes.root, cellClassName)}
                >
                  {cellValue}
                </TableCell>
              );
            })}
          </TableRow>
        </>
      );
    },
    [config]
  );

  if(loading) {
    return <TableSkeleton className={className} />
  }

  return (
    <div className="flex flex-column br2">
      <TableContainer className={clsx(classes.root, className)}>
        <DefaultTable padding={padding} size={size} stickyHeader={stickyHeader}>
          {getTableHeader()}
          <TableBody>
            {data?.map((row, index) => (
              <Fragment key={index}>
                {getTableRow(row, index)}

                {/* Inner table render props */}
                {(typeof innerTableRender === 'function' && innerTableVisibleMap[index]) && innerTableRender(row, index)}
              </Fragment>
            ))}
            {children}
          </TableBody>
        </DefaultTable>
      </TableContainer>
    </div>
  );
};

Table.defaultProps = {
  className: '',
  headerClassName: '',
  stickyHeader: false,
  showHeader: true,
  children: null,
  loading: false
};

export default Table;
export { TableProps, TableConfigProps, TableCellType };
